/**
 * Consent gate modal (Story 463)
 *
 * Blocks app access until user accepts Terms of Service and Privacy Policy.
 * Shown when user is authenticated but has not accepted the current terms version.
 */

import { useState } from 'react';
import { config } from '../config';

interface ConsentGateProps {
  currentTermsVersion: string;
  onAccepted: () => void;
}

export function ConsentGate({ currentTermsVersion, onAccepted }: ConsentGateProps) {
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAccept = async () => {
    if (!agreed || submitting) return;

    setSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`${config.apiUrl}/api/v1/consent/accept`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          terms_version: currentTermsVersion,
          source: 'web',
        }),
      });

      if (!response.ok) {
        const data = await response.json() as { error?: string };
        throw new Error(data.error ?? 'Failed to record consent');
      }

      onAccepted();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1100,
      }}
    >
      <div
        style={{
          backgroundColor: 'var(--bg-subtle)',
          border: '1px solid var(--border-light)',
          borderRadius: '8px',
          padding: '2rem',
          maxWidth: '500px',
          width: '90%',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
        }}
      >
        <h2
          style={{
            fontSize: '1.25rem',
            fontWeight: '600',
            color: 'var(--text-primary)',
            margin: '0 0 1rem 0',
          }}
        >
          Terms of Service &amp; Privacy Policy
        </h2>

        <p
          style={{
            marginBottom: '1rem',
            color: 'var(--text-secondary)',
            lineHeight: '1.6',
          }}
        >
          Before you continue, please review and accept our terms.
        </p>

        <p
          style={{
            marginBottom: '0.5rem',
            color: 'var(--text-secondary)',
            lineHeight: '1.6',
          }}
        >
          By continuing, you agree to our{' '}
          <a
            href={`${config.legalBaseUrl}/tos`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'var(--interactive)', textDecoration: 'underline' }}
          >
            Terms of Service
          </a>{' '}
          and{' '}
          <a
            href={`${config.legalBaseUrl}/privacy`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'var(--interactive)', textDecoration: 'underline' }}
          >
            Privacy Policy
          </a>
          .
        </p>

        <p
          style={{
            marginBottom: '1.5rem',
            color: 'var(--text-muted)',
            fontSize: '0.875rem',
            lineHeight: '1.5',
          }}
        >
          Your specifications are processed by AI services for quality validation.
          See the Privacy Policy for details.
        </p>

        <label
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '0.75rem',
            marginBottom: '1.5rem',
            cursor: 'pointer',
            color: 'var(--text-primary)',
            lineHeight: '1.5',
          }}
        >
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            style={{
              marginTop: '0.2rem',
              width: '18px',
              height: '18px',
              accentColor: 'var(--interactive)',
              cursor: 'pointer',
            }}
          />
          I agree to the Terms of Service and Privacy Policy
        </label>

        {error && (
          <p
            style={{
              marginBottom: '1rem',
              color: 'var(--status-error)',
              fontSize: '0.875rem',
            }}
          >
            {error}
          </p>
        )}

        <button
          onClick={handleAccept}
          disabled={!agreed || submitting}
          style={{
            width: '100%',
            padding: '0.75rem 1.5rem',
            backgroundColor: agreed && !submitting ? 'var(--interactive)' : 'var(--bg-muted)',
            color: agreed && !submitting ? 'var(--text-on-brand)' : 'var(--text-muted)',
            border: '1px solid var(--border-light)',
            borderRadius: '6px',
            fontSize: '1rem',
            fontWeight: '500',
            cursor: agreed && !submitting ? 'pointer' : 'not-allowed',
            transition: 'background-color 0.15s, color 0.15s',
          }}
          onMouseEnter={(e) => {
            if (agreed && !submitting) {
              e.currentTarget.style.backgroundColor = 'var(--interactive-hover)';
            }
          }}
          onMouseLeave={(e) => {
            if (agreed && !submitting) {
              e.currentTarget.style.backgroundColor = 'var(--interactive)';
            }
          }}
        >
          {submitting ? 'Saving...' : 'Continue'}
        </button>
      </div>
    </div>
  );
}
